import React, { useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Import Toastify styles

const UserList = ({ filteredUsers, setSelectedUser }) => {
  const [showModal, setShowModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // Track type of deletion

  // Function to handle delete user
  const handleDeleteUser = async (user, type) => {
    const endpoint = type === 'temporary' ? `/api/users/${user.user._id}/temp` : `/api/users/${user.user._id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting user:', error);
      throw new Error('Failed to delete user');
    }
  };

  const confirmDeleteUser = async (type) => {
    try {
      const response = await handleDeleteUser(userToDelete, type);
      
      if (response.message === 'User temporarily deleted') {
        toast.success('User temporarily deleted.');
      } else if (response.message === 'User permanently deleted') {
        toast.success('User permanently deleted.');
      } else {
        toast.error('Failed to delete the user.');
      }
    } catch (error) {
      toast.error('An error occurred while deleting the user.');
    } finally {
      setShowModal(false);
      setUserToDelete(null);
      setDeleteType('');
    }
  };

  // Function to handle view details
  const handleViewDetails = (user) => {
    setUserDetails(user);
    setShowDetailsModal(true);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Users List</h2>
      <table className="w-full text-left border-collapse">
        <thead className="bg-gray-200 text-gray-600">
          <tr>
            <th className="py-3 px-4 border-b">Name</th>
            <th className="py-3 px-4 border-b">Email</th>
            <th className="py-3 px-4 border-b">Role</th>
            <th className="py-3 px-4 border-b">Block Status</th>
            <th className="py-3 px-4 border-b">Subscription Status</th>
            <th className="py-3 px-4 border-b">Subscription End Date</th>
            <th className="py-3 px-4 border-b">Plan</th>
            <th className="py-3 px-4 border-b">Payment Method</th>
            <th className="py-3 px-4 border-b">Payment Status</th>
            <th className="py-3 px-4 border-b">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((user, index) => (
            <tr
              key={index}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => setSelectedUser(user)}
            >
              <td className="py-3 px-4 border-b">
                {user?.user?.firstname || ''} {user?.user?.lastname || ''}
              </td>
              <td className="py-3 px-4 border-b">{user?.user?.username || ''}</td>
              <td className="py-3 px-4 border-b">{user?.user?.role || ''}</td>
              <td className="py-3 px-4 border-b">{user?.user?.blockStatus || ''}</td>
              <td className="py-3 px-4 border-b">{user?.subscriber?.subscriptionStatus || ''}</td>
              <td className="py-3 px-4 border-b">{user?.subscriber?.subscriptionEndDate || ''}</td>
              <td className="py-3 px-4 border-b">{user?.subscriber?.currentPlan || ''}</td>
              <td className="py-3 px-4 border-b">{user?.payment?.paymentType || ''}</td>
              <td className="py-3 px-4 border-b">{user?.payment?.transactionStatus || ''}</td>
              <td className="py-3 px-4 border-b flex items-center space-x-2">
                {/* Edit Button */}
                <button
                  className="bg-blue-500 text-white px-4 py-2 rounded-sm hover:bg-blue-600 transition duration-200 ease-in-out"
                  onClick={() => setSelectedUser(user)}
                >
                  Edit
                </button>
                {/* View Details Button */}
                <button
                  className="bg-green-500 text-white px-4 py-2 rounded-sm hover:bg-green-600 transition duration-200 ease-in-out"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering row click
                    handleViewDetails(user);
                  }}
                >
                  Details
                </button>
                {/* Delete Button with Trash Icon */}
                <button
                  className="text-red-500 hover:text-red-700 transition duration-200 ease-in-out"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering row click
                    setUserToDelete(user);
                    setShowModal(true);
                  }}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Modal for Delete Confirmation */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Choose an option to delete this user:
            </h3>
            <div className="flex justify-around mb-4">
              <button
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600"
                onClick={() => {
                  confirmDeleteUser('temporary'); // Call with 'temporary' type
                }}
              >
                Temporary Delete
              </button>
              <button
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
                onClick={() => {
                  confirmDeleteUser('permanent'); // Call with 'permanent' type
                }}
              >
                Permanent Delete
              </button>
            </div>
            <div className="flex justify-end">
              <button
                className="bg-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Viewing User Details */}
      {showDetailsModal && userDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full">
            <h3 className="text-lg font-bold text-gray-800 mb-4">User Details</h3>

            {/* User Details */}
            <p><strong>Name:</strong> {userDetails?.user?.firstname} {userDetails?.user?.lastname}</p>
            <p><strong>Email:</strong> {userDetails?.user?.username}</p>
            <p><strong>Role:</strong> {userDetails?.user?.role}</p>
            <p><strong>Block Status:</strong> {userDetails?.user?.blockStatus}</p>
            <p><strong>Subscription Status:</strong> {userDetails?.subscriber?.subscriptionStatus}</p>
            <p><strong>Subscription End Date:</strong> {userDetails?.subscriber?.subscriptionEndDate}</p>
            <p><strong>Plan:</strong> {userDetails?.subscriber?.currentPlan}</p>

            {/* Payment Details */}
            <p><strong>Payment Type:</strong> {userDetails?.payment?.paymentType}</p>
            <p><strong>Transaction Status:</strong> {userDetails?.payment?.transactionStatus}</p>

            <div className="flex justify-end mt-4">
              <button
                className="bg-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />

    </div>
  );
};

export default UserList;
